/**
 * The player's hull gates (M10 P5a1b1, plan 2026-10-17-m10-p5a §3.2, hull tiers spec §6.8), from `src/data/shipyard.json`.
 *
 * - **Gates by band × civil | combat:** a hull whose role is in `combatRoles` is a combat hull; `byRole` overrides its
 *   band's combat gate (S: interceptor 10, gunship 20, today's `standingReq`). A gate lists the steps it needs:
 *   `leaseOwned`, `makerStanding` (at least that), `corporation` (a founded company), `armsLicence` (the Charter's;
 *   no player licence exists yet, so the step stays missing).
 * - **The standing a gate reads** is the hull maker's; a licence build (the yard owner holds a `hull-lines.json`
 *   licence for the hull's line) reads the yard owner's, and a hull without a maker the yard owner's too.
 * - **Keys off:** with `enabled` false `gateStatus` names no missing step and `standingToSend` is today's (the yard
 *   owner's), so the shop, the service and the worker's `buyShip` command are unchanged until the W6 flip (review F2).
 *   `catalogueRows` (P5a1b2) and `devicesLive` (P5a1c) are read by their cards.
 * - The worker's `standingReq` refusal (`strategic/fleet/purchase.ts`) stays as the backstop.
 *
 * Pure.
 */
import { z } from 'zod'
import type { Career } from '../../../career/types'
import hullsJson from '../../../data/strategic/hulls.json'
import { SIM_FACTION_IDS, type SimFactionId } from '../../../data/m2Schemas'
import shipyardJson from '../../../data/shipyard.json'
import { HULL_BAND_IDS, HULL_ROLE_IDS, loadHullLines, type HullBand, type HullLines, type HullRoleId } from '../../../strategic/hullLines'

const stepsSchema = z.strictObject({
  leaseOwned: z.literal(true).optional(),
  makerStanding: z.number().min(-100).max(100).optional(),
  corporation: z.literal(true).optional(),
  armsLicence: z.literal(true).optional(),
})
export type GateSteps = z.infer<typeof stepsSchema>

const bandGateSchema = z.strictObject({
  civil: stepsSchema,
  combat: stepsSchema,
  byRole: z.partialRecord(z.enum(HULL_ROLE_IDS), stepsSchema).optional(),
})

export const shipyardRulesSchema = z.strictObject({
  comment: z.string().optional(),
  /** The gates refuse (shop and service) and the maker's standing is sent to the worker; off until W6. */
  enabled: z.boolean(),
  /** P5a1b2: the shop's rows from the yard's tooling catalogue; off until W6. */
  catalogueRows: z.boolean(),
  /** P5a1c: the role devices that work (the device rule); empty until W6. */
  devicesLive: z.array(z.string().min(1)).refine((a) => new Set(a).size === a.length, { message: 'duplicate device' }),
  combatRoles: z.array(z.enum(HULL_ROLE_IDS)).refine((a) => new Set(a).size === a.length, { message: 'duplicate role' }),
  gates: z.strictObject(Object.fromEntries(HULL_BAND_IDS.map((b) => [b, bandGateSchema])) as Record<HullBand, typeof bandGateSchema>),
}).superRefine((r, ctx) => {
  // M10 P5a1b2 (W2 review G5): `gateOf` reads `byRole` only for a combat role, so a civil role's entry would be
  // silently ignored. Refuse it.
  for (const band of HULL_BAND_IDS) {
    for (const role of Object.keys(r.gates[band].byRole ?? {})) {
      if (!(r.combatRoles as readonly string[]).includes(role)) {
        ctx.addIssue({ code: 'custom', path: ['gates', band, 'byRole', role], message: `byRole.${role} is not a combat role (gateOf ignores it)` })
      }
    }
  }
})
export type ShipyardRules = z.infer<typeof shipyardRulesSchema>

let rulesMemo: ShipyardRules | null = null

/** The shipped `shipyard.json` (parsed once); tests parse a copy with `parseShipyardRules`. */
export function shipyardRules(): ShipyardRules {
  rulesMemo ??= Object.freeze(shipyardRulesSchema.parse(shipyardJson))
  return rulesMemo
}

export function parseShipyardRules(raw: unknown): ShipyardRules {
  return shipyardRulesSchema.parse(raw)
}

/** What a gate needs to know about a hull. */
export interface GateHull {
  id: string
  band: HullBand
  role: HullRoleId | null
  /** The maker faction id (`hulls.json maker`), or null. */
  maker: string | null
  /** The hull's line id (`hulls.json line`), or null. */
  line: string | null
}

/** What a gate needs to know about the player. */
export interface GatePlayer {
  leaseOwned: boolean
  /** A founded company (not a sole trader). */
  corporation: boolean
  /** A Charter arms licence (none exists yet: false). */
  armsLicence: boolean
  /** The Career's standing with faction `id` (0 for one it keeps none with). */
  standingOf(id: string): number
}

/** The gate's view of the Career: lease, standings; `corporation`: a founded company. No player arms licence yet. */
export function gatePlayerOf(c: Pick<Career, 'lease' | 'standings'>, corporation: boolean): GatePlayer {
  return {
    leaseOwned: c.lease.owned,
    corporation,
    armsLicence: false,
    standingOf: (id) => ((SIM_FACTION_IDS as readonly string[]).includes(id) ? c.standings[id as SimFactionId] : 0),
  }
}

/** The yard: its owner and the player's standing with it (today's sent standing). */
export interface GateYard {
  owner: string | null
  standing: number
}

export type GateStep =
  | { kind: 'leaseOwned' }
  | { kind: 'corporation' }
  | { kind: 'armsLicence' }
  | { kind: 'makerStanding'; faction: string | null; need: number; have: number }

export interface GateStatus {
  kind: 'civil' | 'combat'
  /** The faction whose standing the gate reads (null: the yard's standing, no faction known). */
  standingFaction: string | null
  /** The standing the gate reads. */
  standing: number
  /** The steps still missing, in gate order; empty when the gate is met or the gates are off. */
  missing: GateStep[]
}

const hullRowsShape = z.object({
  hulls: z.array(
    z.object({
      id: z.string(),
      band: z.enum(HULL_BAND_IDS).optional(),
      line: z.string().optional(),
      maker: z.string().optional(),
      class: z.string().optional(),
      sale: z.boolean().optional(),
      story: z.boolean().optional(),
      retired: z.boolean().optional(),
    }),
  ),
})

/** Hull id → role from `hull-lines.json` (a line's role; a variant's own or its base's), as `catalog.ts` reads it. */
function rolesOf(lines: HullLines): Map<string, HullRoleId> {
  const role = new Map<string, HullRoleId>()
  for (const l of lines.lines) if (l.role !== undefined) for (const h of l.hulls) role.set(h.id, l.role)
  for (const v of lines.variants) {
    const r = v.role ?? role.get(v.base)
    if (r !== undefined) role.set(v.id, r)
  }
  return role
}

/**
 * Every hull row with a band as a `GateHull` (default: the shipped `hulls.json` and `hull-lines.json`). M10 P5a1b2
 * (W2 review G5): a row the shop sells (a `class`, not `sale: false`, story or retired; `catalog.ts`'s rule) with no
 * band would be silently ungated, so it throws.
 */
export function buildGateHulls(hullsRaw: unknown = hullsJson, lines: HullLines = loadHullLines()): ReadonlyMap<string, GateHull> {
  const roles = rolesOf(lines)
  const out = new Map<string, GateHull>()
  for (const h of hullRowsShape.parse(hullsRaw).hulls) {
    if (h.band === undefined) {
      if (h.class !== undefined && h.sale !== false && h.story !== true && h.retired !== true) {
        throw new Error(`hulls.json: sold hull ${h.id} has no band, so no gate reads it`)
      }
      continue
    }
    out.set(h.id, Object.freeze({ id: h.id, band: h.band, role: roles.get(h.id) ?? null, maker: h.maker ?? null, line: h.line ?? null }))
  }
  return out
}

let hullsMemo: ReadonlyMap<string, GateHull> | null = null

/** The shipped hull `id`'s gate data, or null (no band: not a player hull). */
export function gateHullOf(id: string): GateHull | null {
  hullsMemo ??= buildGateHulls()
  return hullsMemo.get(id) ?? null
}

/** A licence build: the yard owner holds a licence for the hull's line from another maker. */
export function isLicenceBuild(hull: GateHull, owner: string | null, lines: HullLines = loadHullLines()): boolean {
  if (owner === null || hull.line === null || owner === hull.maker) return false
  return lines.licences.some((l) => l.licensee === owner && l.line === hull.line)
}

/** The gate for `hull`: its band's civil or combat gate, or the role's override. */
export function gateOf(hull: GateHull, rules: ShipyardRules = shipyardRules()): { kind: 'civil' | 'combat'; steps: GateSteps } {
  const band = rules.gates[hull.band]
  const combat = hull.role !== null && rules.combatRoles.includes(hull.role)
  if (!combat) return { kind: 'civil', steps: band.civil }
  const own = hull.role === null ? undefined : band.byRole?.[hull.role]
  return { kind: 'combat', steps: own ?? band.combat }
}

/**
 * The gate status of `hull` for `player` at `yard`: which steps are missing (none while `rules.enabled` is false)
 * and whose standing it reads (the maker's; a licence build or a maker-less hull: the yard owner's).
 */
export function gateStatus(player: GatePlayer, hull: GateHull, yard: GateYard, rules: ShipyardRules = shipyardRules(), lines: HullLines = loadHullLines()): GateStatus {
  const { kind, steps } = gateOf(hull, rules)
  const yardRead = hull.maker === null || isLicenceBuild(hull, yard.owner, lines)
  const standingFaction = yardRead ? yard.owner : hull.maker
  const standing = yardRead ? yard.standing : player.standingOf(hull.maker ?? '')
  const missing: GateStep[] = []
  if (!rules.enabled) return { kind, standingFaction, standing, missing }
  if (steps.leaseOwned === true && !player.leaseOwned) missing.push({ kind: 'leaseOwned' })
  if (steps.corporation === true && !player.corporation) missing.push({ kind: 'corporation' })
  if (steps.makerStanding !== undefined && !(standing >= steps.makerStanding)) {
    missing.push({ kind: 'makerStanding', faction: standingFaction, need: steps.makerStanding, have: standing })
  }
  if (steps.armsLicence === true && !player.armsLicence) missing.push({ kind: 'armsLicence' })
  return { kind, standingFaction, standing, missing }
}

/**
 * The standing the purchase command carries: today's (the yard owner's, `yard.standing`) while the gates are off
 * (review F2: S/M purchases byte-identical), the gate's (the maker's) once `enabled`.
 */
export function standingToSend(player: GatePlayer, hull: GateHull | null, yard: GateYard, rules: ShipyardRules = shipyardRules()): number {
  if (!rules.enabled || hull === null) return yard.standing
  return gateStatus(player, hull, yard, rules).standing
}

/** One missing step's line: "Lease must be owned", "Tethys standing 25 (you: 12)". `nameOf` names a faction. */
export function gateLine(step: GateStep, nameOf: (id: string) => string): string {
  switch (step.kind) {
    case 'leaseOwned':
      return 'Lease must be owned'
    case 'corporation':
      return 'Found a corporation first'
    case 'armsLicence':
      return 'Needs a Charter arms licence'
    case 'makerStanding':
      return `${step.faction === null ? 'Standing' : `${nameOf(step.faction)} standing`} ${Math.round(step.need)} (you: ${Math.round(step.have)})`
  }
}

/**
 * What the yard-tooling rule reads of a hull (M10 P5a1b2): the main-thread copy of the worker's hull tables
 * (`strategic/data.ts` `hullMaker`, `hullLine`, `hullFlags`), from `hull-lines.json` alone. A line hull: its line's
 * maker and line, its own flags; an unlined hull: no maker, no line, its own flags; a variant: its base's maker and
 * line, its base's flags but retired, plus its own. A hull outside `hull-lines.json` is in no map (no tooling builds it).
 */
export interface ToolingHull {
  maker: string | null
  line: string | null
  export: boolean
  retired: boolean
}

export function buildToolingHulls(lines: HullLines = loadHullLines()): ReadonlyMap<string, ToolingHull> {
  const out = new Map<string, ToolingHull>()
  for (const l of lines.lines) {
    for (const h of l.hulls) out.set(h.id, { maker: l.maker, line: l.id, export: h.export === true, retired: h.retired === true })
  }
  for (const u of lines.unlined) out.set(u.id, { maker: null, line: null, export: u.export === true, retired: u.retired === true })
  for (const v of lines.variants) {
    const b = out.get(v.base)
    if (b === undefined) continue
    out.set(v.id, { maker: b.maker, line: b.line, export: b.export || v.export === true, retired: v.retired === true })
  }
  return out
}

/**
 * The main-thread copy of the worker's `toolingBuilds` (`strategic/catalogue.ts`, module-private; P4b's file, not
 * edited): tooling faction `tooling` builds `hull` when it is an export pattern, the tooling's own line, or a line the
 * tooling holds a licence for. `tooling` null (the player slot, no maker) builds only export patterns. The
 * equivalence test (`gates.tooling.test.ts`) checks it against `buildCatalogue` on the shipped data.
 */
export function toolingBuildsHull(tooling: string | null, hull: ToolingHull, lines: HullLines = loadHullLines()): boolean {
  if (hull.export) return true
  if (tooling === null) return false
  if (hull.maker === tooling) return true
  const line = hull.line
  return line !== null && lines.licences.some((l) => l.licensee === tooling && l.line === line)
}

let toolingMemo: ReadonlyMap<string, ToolingHull> | null = null

/**
 * A yard with tooling `tooling` builds hull `id` (the catalogue's row rule: not retired, and `toolingBuildsHull`).
 * Default data: the shipped `hull-lines.json`; a test passes a copy.
 */
export function yardBuilds(tooling: string | null, id: string, lines?: HullLines): boolean {
  const map = lines === undefined ? (toolingMemo ??= buildToolingHulls()) : buildToolingHulls(lines)
  const h = map.get(id)
  return h !== undefined && !h.retired && toolingBuildsHull(tooling, h, lines)
}
